/* @flow */
/* eslint-env es6,browser */

import adconfig from '../adconfig';
import adloader from '../adloader';
import containerHelper from '../container-helper';
import _adserverbase from './adserverbase';
import { BrowserUtil } from '../utils/browserutil';
import { logError, logDebug, isdebugEnabled } from '../utils/logger';
import { extend, isNumber } from '../utils/coreutils';
import { getCCPA } from '../utils/ccpautils';

/**
 * Implements appnexus ad server functionality
 *
 * @class
 * @exports adserver/appnexus
 * @requires  module:coreutils
 * @requires  module:adconfig
 * @requires  module:adloader
 * @requires  module:adserverbase
 * @requires module:containerHelper
 * @requires  module:browserutil
 * @requires module:logger
 */

class _appnexus extends _adserverbase {
  _cVars: { [string]: CustomVars };

  _libAlreadyLoaded: boolean;

  _oldLib: string;

  _defConfig: appnexusConfig;

  _config: appnexusConfig;

  _targetToPos: { [string]: string };

  constructor(name: string) {
    super();
    this._name = name;
    this._defConfig = extend(true, {}, this._defConfig, {
      lib: '//acdn.adnxs.com/ast/ast.js',
      url: 'https://secure.adnxs.com/ttj?',
      timeout: 10000,
      id: '7485'
    });
    this._config = extend(true, this._defConfig);
    this._cVars = {}; // Store position's custom vars to pass on to ad loader once ad is available
    this._libAlreadyLoaded = false;
    this._oldLib = ''; // Store older lib url to prevent re-loading same lib again after updating config
    this._targetToPos = {}; // Store targetId to position name map
  }

  /**
   * Initialize the object - called from adserver initialize
   *
   * @param {window} w - window object
   * @param {Document} d - document object
   * @param {module:globalParams.globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: window, d: Document, g: globalParam) {
    super.init(w, d, g);
    this._win.apntag = this._win.apntag || {}; // Apnexus library handle
    this._win.apntag.anq = this._win.apntag.anq || []; // Apnexus library handle request quue
    this._win.apntag.anq.push(() => {
      const apnTag: apntagObj = this._win.apntag;
      if (isdebugEnabled()) {
        apnTag.enableDebug();
      }
      apnTag.onEvent('adAvailable', (adObj: asResponseObject) => {
        // Event is triggered once ad content is available
        const position = this._getPosFromTargetId(adObj);
        if (position.length) {
          adloader.load(position, '', this._cVars[position], this._name);
        }
      });
      apnTag.onEvent('adNoBid', adObj => {
        // Event is triggered if the appnexus server request has 'nobid' value as true
        const position = this._getPosFromTargetId(adObj);
        logError(`${this._name} No bid response for position : ${position}`);
      });
      apnTag.onEvent('adRequestFailure', (adObj: asRespError) => {
        // Event is triggered when ad request has failed
        logError(
          `${this._name} Request failure for position ${adObj.targetId ||
            ''} with error : ${adObj.errMessage || ''}`
        );
      });
      apnTag.onEvent('adBadRequest', adObj => {
        // Event is triggered in case of an invalid ad request
        logError(
          `${this._name} Bad request for position ${adObj.targetId ||
            ''} with error : ${adObj.errMessage || ''}`
        );
      });
    });
  }

  _preUpdateConfig() {
    this._oldLib = this._config.lib || '';
  }

  _initAdLibrary() {
    const apnTag: apntagObj = this._win.apntag;
    apnTag.anq.push(() => {
      apnTag.setPageOpts({
        member: this._config.id
      });
    });
  }

  _postUpdateConfig() {
    // Do not load already loaded library
    if (this._libAlreadyLoaded && this._oldLib === this._config.lib) {
      return;
    }

    // load the library only for modern browsers
    if (BrowserUtil.isOldIE()) {
      return;
    }
    this._loadLibrary(this._config.lib);
    this._libAlreadyLoaded = true;
    this._initAdLibrary();
  }

  clearAdRequests() {
    const apnTag: apntagObj = this._win.apntag;
    apnTag.anq.push(() => {
      // clear all pending requests
      apnTag.clearRequest();
    });
    // reinitialize the config again
    this._initAdLibrary();
    // reset the position specific cvars we cached
    this._cVars = {};
    this._targetToPos = {};
  }

  /**
   * Reverse lookup position name from targetId defined
   *
   * @param {object} response - Appnexus response object
   * @returns {string} - Position name
   * @private
   */
  _getPosFromTargetId(response: asResponseObject): string {
    let position = '';
    const { tagId = 0, targetId = '' } = response;
    if (!targetId.length) {
      return this._getPosFromTagId(tagId);
    }
    position = this._targetToPos[targetId] || '';
    return position;
  }

  /**
   * Reverse lookup position name from tagid defined
   *
   * @param {number} tagId - string
   * @returns {string} - Position name
   * @private
   */
  _getPosFromTagId(tagId: number = 0): string {
    let position = '';
    if (tagId === 0) {
      return position;
    }
    const posList = adconfig.getPagePositions();
    position =
      posList.find(pos => {
        const posConf = adconfig.getPosSettings(pos);
        return isNumber(posConf.tagId) && posConf.tagId === tagId;
      }) || position;
    return position;
  }

  _getTagID(pos: string = ''): number {
    return adconfig.getPosSettings(pos).tagId || 0;
  }

  _getSizes(pos: string = ''): posSizes {
    return adconfig.getPosSettings(pos).sizes || [];
  }

  /**
   * Builds script tag for each ad position from the definetag object
   *
   * @param {object} dt - defineTag object
   * @returns {string} - Ad creative to render
   * @memberof _appnexus
   */
  _getAdContent(dt: tagObject): string {
    const tagID = dt.tagId;
    const { sizes } = dt;
    let size = '';
    if (sizes.length) {
      const sz = sizes.shift();
      size = sz.join('x');
    }
    if (tagID === 0 || size.length === 0) {
      return '';
    }
    const ccpa = getCCPA();
    // pass the keywords as query params here
    const kw = Object.entries(dt.keywords)
      .map(([k, v]) => `${k}=${String(v)}`)
      .join('&');
    return `<script type='text/javascript' src='${this._config.url}id=${tagID}&size=${size}&us_privacy=${ccpa}&${kw}'></script>`;
  }

  /**
   * Load each ad position using old script method
   *
   * @param {object} o - define tag object
   * @memberof _appnexus
   * @returns {undefined}
   */
  loadAdsNoAST(o: tagMap) {
    Object.keys(o).forEach((pos: string) => {
      const dt = o[pos];
      const adContent = this._getAdContent(dt);
      if (adContent.length) {
        adloader.load(pos, adContent, this._cVars[pos], '', true);
      }
    });
  }

  /**
   * Loads openx static using adloader
   *
   * @typedef {object} hbPosData
   * @param {Array<string>} posList - position list array
   * @param {object.<string, hbPosData>} o - header bidding return object
   * @param {object} customVars - custom variable to be populated inside iframe
   * @param {boolean} bRotation - For ad rotation?
   * @returns {undefined}
   */
  loadAds(
    posList: Array<string>,
    o: hbPartnerData,
    customVars: CustomVars,
    bRotation: boolean
  ) {
    posList = posList || [];
    o = o || {};
    customVars = customVars || {};
    posList.forEach(pos => {
      this._cVars[pos] = this._getCustomVars(pos, o, customVars, bRotation);
    });

    // data required for defineTag is created as pos=>object map
    const defineTagMap: tagMap = {};
    posList.forEach(pos => {
      // ensure that containers are created before we load ads
      containerHelper.createContainers(pos);
      // Get inactive container div of the ad position
      const adContainerFrameIds: adInfo = containerHelper.getAdContainerInfo(
        pos,
        false
      );
      this._targetToPos[adContainerFrameIds.cID] = pos;
      const qparms = this._getQueryParams(pos, o, bRotation) || {};
      defineTagMap[pos] = {
        tagId: this._getTagID(pos),
        sizes: this._getSizes(pos),
        targetId: adContainerFrameIds.cID,
        keywords: qparms
      };
    });

    const apnTag: apntagObj = this._win.apntag;
    if (apnTag && apnTag.anq && this._libAlreadyLoaded) {
      logDebug(`${this._name} queueing up ${posList.join(',')}`);
      // apntag is loaded & available. Let's use their AST library
      apnTag.anq.push(() => {
        try {
          Object.keys(defineTagMap)
            .map((pos: string) => defineTagMap[pos])
            .forEach((obj: tagObject) => apnTag.defineTag(obj));
          apnTag.loadTags(); // Send request to appnexus adserver
        } catch (error) {
          logError(
            `${this._name} appnexus library calls failure ${error.message}`
          );
        }
      });
    } else {
      logDebug(`${this._name} rendering ${posList.join(',')} using doc.write`);
      // fallback to script tag for old browser
      this.loadAdsNoAST(defineTagMap);
    }
    adloader.checkQueue();
  }
}
const appnexus = new _appnexus('appnexusAS');
export default appnexus;
